import {AdobeTrackActionActions, MenuSection, PlayerStats, RequestState} from "modules/enums";
import {Nullable, NullOrEmpty} from "modules/types";
import RallySSO from "modules/utils/SSO/rallySSO";
import {CATEGORY_MAPPER, EXIT_URL, LEADERBOARD_URL, MENU_SECTIONS_MAPPER} from "modules/constants";
import {AdobeAnalyticService} from "modules/utils/Services/AdobeAnalyticService";

export function withTimeStamp(path: string): string {
	const t = new Date().getTime().toString();
	return `${path}?t=${t}`;
}

export function getDashedValue(value: NullOrEmpty<string | number>) {
	if (value === null || value === undefined) {
		return "-";
	}

	return String(value);
}

export function parseStateType(state: RequestState) {
	const isIdle = state === RequestState.IDLE;
	const isLoading = state === RequestState.Requested;
	const isSuccess = state === RequestState.Received;
	const isLoadingOrSuccess = isLoading || isSuccess;

	return {isIdle, isLoading, isSuccess, isLoadingOrSuccess};
}

export function checkIsInputInvalid(name: string, value: string) {
	if (!name) {
		return true;
	}

	return value === null || value === undefined;
}

export function getNotBelowZero(value: NullOrEmpty<number>) {
	if (value === undefined || value === null) {
		return 0;
	}
	const numberValue = parseFloat(String(value));
	return numberValue < 0 ? 0 : numberValue;
}

export function calculateProgress(progress: number, color1: string, color2: string = "#161616") {
	const to = (360 / 100) * progress;

	return `${color1} 0deg, ${color1} ${to}deg, ${color2} ${to}deg, ${color2} 360deg`;
}

export function isBoolean(value: NullOrEmpty<boolean>) {
	return value === true || value === false;
}

export function disableBodyScroll(disable: boolean) {
	if (!document || !document.body || !document.body.classList) {
		console.warn("No document.body found!");
		return;
	}

	if (disable) {
		document.body.classList.add("no-scroll");
	} else {
		document.body.classList.remove("no-scroll");
	}
}

export function getClassAccordingBoolean(className: string, value: NullOrEmpty<boolean>) {
	return value ? className : "";
}

export function openLeaderboardsNative(contest: Nullable<string>) {
	if (contest === null) {
		console.warn("Invalid contest");
		return;
	}

	if (RallySSO.IS_NATIVE) {
		RallySSO.nativeBridge?.openLeaderboard();
	} else {
		window.open(LEADERBOARD_URL.replace("--X--", contest), "_blank");
	}
}

export function closeApplication() {
	if (RallySSO.IS_NATIVE) {
		RallySSO.nativeBridge?.close();
	} else {
		window.location.href = EXIT_URL;
	}
}

export function openExternalURL(url: string) {
	if (RallySSO.IS_NATIVE) {
		RallySSO.nativeBridge?.launchExternal(url);
	} else {
		window.open(url, "_blank");
	}
}

export function isNumberDecimal(value: number) {
	return value % 1 > 0;
}

export function isNullOrEmpty<T>(value: NullOrEmpty<T>): boolean {
	return value === null || value === undefined;
}

function prepareByCategory(stat: NullOrEmpty<number>, category: NullOrEmpty<string> = "") {
	if (category === PlayerStats.ERA) {
		return {
			stat: stat!.toFixed(2),
			prepared: true,
		};
	}

	if (category === PlayerStats.FASTEST_PITCH || category === PlayerStats.HIGHEST_EXIT_VELOCITY) {
		return {
			stat: stat!.toFixed(1),
			prepared: true,
		};
	}

	return {
		stat,
		prepared: false,
	};
}

export function prepareStatToRender(
	stat: NullOrEmpty<number>,
	category: NullOrEmpty<string> = ""
): string {
	if (isNullOrEmpty(stat)) {
		return "-";
	}

	const byCategory = prepareByCategory(stat, category);
	if (byCategory.prepared) {
		return byCategory.stat!.toString();
	}

	if (category === PlayerStats.BATTING_AVG) {
		const isLowerThanOne = stat! < 1;
		if (isLowerThanOne) {
			return stat!.toFixed(3).substring(1);
		}
		return stat!.toFixed(3);
	}

	return stat!.toString();
}

export function getIsUserDeviceIsIOS() {
	const userAgent = navigator.userAgent;
	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
	// iPad on iOS 13 detection
	const isMac = userAgent.includes("Mac") && "ontouchend" in document;

	return (
		["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(
			navigator.platform
		) ||
		isSafari ||
		isMac
	);
}

export function getLocallyTranslatedCategory(category: NullOrEmpty<PlayerStats>) {
	if (!category) {
		return "";
	}

	return CATEGORY_MAPPER[category];
}

export function handleMenuAnalyticsClick(section: MenuSection, link = window.location.href) {
	AdobeAnalyticService.TrackAction({
		"action.name": AdobeTrackActionActions.MenuAction.replace("--X--", section),
		"action.element.text": section,
		"action.element.targetUrl": link,
		"action.element.index": MENU_SECTIONS_MAPPER[section] || 0,
	});
}

export function numOrZero(num: number | undefined | null) {
	return num ?? 0;
}

export const buildWithPlural = (num: number) => {
	const pr = new Intl.PluralRules("en-US", {type: "ordinal"});

	const suffixes = new Map([
		["one", "st"],
		["two", "nd"],
		["few", "rd"],
		["other", "th"],
	]);

	const rule = pr.select(num);
	const suffix = suffixes.get(rule);

	if (suffix) {
		return `${String(num)}${String(suffix)}`;
	}

	return String(num);
};

export function getClassByRank(rank: number) {
	if (rank === 1) {
		return "gold";
	}

	if (rank === 2) {
		return "silver";
	}

	if (rank === 3) {
		return "bronze";
	}

	return "";
}
